import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

async function fetchInstructor(instructorId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}&select=*,institutions(name)&limit=1`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchInstitutions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions?select=id,name&order=name.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function updateInstructor(instructorId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

export default function InstructorInformation({ session }) {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [institutions, setInstitutions] = useState([]);

  useEffect(() => {
  setLoading(true);
  setError('');

  const loads = [fetchInstructor(session.instructorId)];
  if (session.isSuperadmin) loads.push(fetchInstitutions());

  Promise.all(loads)
    .then(([data, inst]) => {
      setInstructors(Array.isArray(data) ? data : []);
      if (inst) setInstitutions(Array.isArray(inst) ? inst : []);
    })
    .catch(() => setError('Failed to load instructor information.'))
    .finally(() => setLoading(false));
}, [session.instructorId, session.isSuperadmin]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
     const updateData = {
  name: editing.name?.trim() || null,
  office: editing.office?.trim() || null,
  office_hours: editing.office_hours?.trim() || null
};

if (session.isSuperadmin) {
  updateData.institution_id = editing.institution_id || null;
}

const result = await updateInstructor(session.instructorId, updateData);
      setInstructors(prev => prev.map(i => i.id === editing.id ? result[0] : i));
      setEditing(null);
      setSuccess('Saved.');
    } catch (err) {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoUpload(instructor, file) {
    if (!file || instructor.id !== session.instructorId) return;
    setUploadingPhoto(prev => ({ ...prev, [instructor.id]: true }));
    setError('');
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];
        const mimeType = file.type;

        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'uploadInstructorPhoto',
            instructorId: session.instructorId,
            base64Data,
            mimeType
          })
        });

        // Poll for updated file ID
        for (let i = 0; i < 10; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const checkResponse = await fetch(
            `${SUPABASE_URL}/rest/v1/instructors?id=eq.${session.instructorId}&select=photo_drive_file_id`,
            { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
          );
          const checkData = await checkResponse.json();
          if (checkData[0]?.photo_drive_file_id) {
            setInstructors(prev => prev.map(inst =>
              inst.id === instructor.id
                ? { ...inst, photo_drive_file_id: checkData[0].photo_drive_file_id }
                : inst
            ));
            setSuccess('Photo uploaded.');
            break;
          }
        }

        setUploadingPhoto(prev => ({ ...prev, [instructor.id]: false }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload photo.');
      setUploadingPhoto(prev => ({ ...prev, [instructor.id]: false }));
    }
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Instructor Information</h2>
      <p style={styles.muted}>
        Edit your instructor information. This information is displayed to students in the student portal.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.list}>
        {instructors.length === 0 && !error && (
          <div style={styles.muted}>No instructor information found for this account.</div>
        )}
        {instructors.map(instructor => (
          <div key={instructor.id} style={styles.item}>
            {editing?.id === instructor.id ? (
              <form onSubmit={handleSave}>
                <div style={styles.editTop}>
                  <div style={styles.photoCol}>
                    <div style={styles.photoBox}>
                      {instructor.photo_drive_file_id ? (
                        <img
                          src={`https://lh3.googleusercontent.com/d/${instructor.photo_drive_file_id}`}
                          alt={instructor.name}
                          style={styles.photo}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={styles.photoPlaceholder}>👤</div>
                      )}
                    </div>
                    <label style={styles.uploadLabel}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => handlePhotoUpload(instructor, e.target.files[0])}
                      />
                      <span style={styles.uploadBtn}>
                        {uploadingPhoto[instructor.id] ? 'Uploading...' : 'Upload photo'}
                      </span>
                    </label>
                  </div>
                  <div style={styles.fieldsCol}>
                    <div style={styles.field}>
                      <label style={styles.label}>Name</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={editing.name || ''}
                        onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Email</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={editing.email || ''}
                        disabled
                      />
                    </div>
                    <div style={styles.field}>
  <label style={styles.label}>Institution</label>
  {session.isSuperadmin ? (
    <select
      style={styles.input}
      value={editing.institution_id || ''}
      onChange={e => setEditing(prev => ({ ...prev, institution_id: e.target.value }))}
    >
      <option value="">— None —</option>
      {institutions.map(inst => (
        <option key={inst.id} value={inst.id}>{inst.name}</option>
      ))}
    </select>
  ) : (
    <input
      style={styles.input}
      type="text"
      value={instructor.institutions?.name || '—'}
      disabled
    />
  )}
</div>
                    <div style={styles.field}>
                      <label style={styles.label}>Office</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={editing.office || ''}
                        onChange={e => setEditing(prev => ({ ...prev, office: e.target.value }))}
                        placeholder="e.g. Building A, Room 301"
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Office hours</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={editing.office_hours || ''}
                        onChange={e => setEditing(prev => ({ ...prev, office_hours: e.target.value }))}
                        placeholder="e.g. Tuesday 2–4pm"
                      />
                    </div>
                  </div>
                </div>
                <div style={styles.btnRow}>
                  <button style={styles.btn} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" style={styles.secondaryBtn} onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div style={styles.itemInner}>
                <div style={styles.itemLeft}>
                  <div style={styles.itemPhotoBox}>
                    {instructor.photo_drive_file_id ? (
                      <img
                        src={`https://drive.google.com/uc?export=view&id=${instructor.photo_drive_file_id}`}
                        alt={instructor.name}
                        style={styles.photo}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={styles.photoPlaceholder}>👤</div>
                    )}
                  </div>
                  <div>
                    <div style={styles.itemName}>{instructor.name || '(No name set)'}</div>
                    <div style={styles.itemEmail}>{instructor.email}</div>
                    <div style={styles.itemEmail}>{instructor.email}</div>
{instructor.institutions?.name && (
  <div style={styles.itemDetail}>🏛 {instructor.institutions.name}</div>
)}
{instructor.office && (
  <div style={styles.itemDetail}>📍 {instructor.office}</div>
)}
                    {instructor.office && (
                      <div style={styles.itemDetail}>📍 {instructor.office}</div>
                    )}
                    {instructor.office_hours && (
                      <div style={styles.itemDetail}>🕐 {instructor.office_hours}</div>
                    )}
                  </div>
                </div>
                <button
                  style={styles.editBtn}
                  onClick={() => { setEditing({ ...instructor }); setError(''); setSuccess(''); }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  item: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    padding: '16px', background: 'white'
  },
  itemInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
  itemLeft: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
  itemPhotoBox: {
    width: '64px', height: '64px', borderRadius: '8px', overflow: 'hidden',
    border: '1px solid #d7dee5', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f0f4f7'
  },
  photo: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { fontSize: '28px' },
  itemName: { fontWeight: '700', fontSize: '15px', color: '#1f2933', marginBottom: '2px' },
  itemEmail: { fontSize: '13px', color: '#66788a', marginBottom: '4px' },
  itemDetail: { fontSize: '13px', color: '#42515f', marginBottom: '2px' },
  editTop: { display: 'flex', gap: '20px', marginBottom: '16px', flexWrap: 'wrap' },
  photoCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 },
  photoBox: {
    width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden',
    border: '1px solid #d7dee5', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f0f4f7'
  },
  fieldsCol: { flex: 1, minWidth: '200px' },
  field: { marginBottom: '12px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  uploadLabel: { cursor: 'pointer' },
  uploadBtn: {
    background: '#f0f4f7', border: '1px solid #d7dee5', borderRadius: '6px',
    padding: '5px 10px', fontSize: '12px', cursor: 'pointer', display: 'inline-block'
  },
  btnRow: { display: 'flex', gap: '10px' },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#66788a', border: '1px solid #d7dee5',
    borderRadius: '8px', padding: '9px 16px', fontSize: '14px', cursor: 'pointer'
  },
  editBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer',
    flexShrink: 0
  }
};